import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { normalizeProjectStatus } from '@/lib/statusNormalize';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ClipboardList, FolderOpen, Calendar, AlertTriangle, CheckCircle2, Clock, Plus, X, Filter } from 'lucide-react';
import logger from '@/lib/logger';

function Projects({ user }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');
  const [formData, setFormData] = useState({
    name: '',
    client_email: '',
    project_type: '',
    location: '',
    start_date: '',
    estimated_end_date: '',
  });
  
  // New project type
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  
  // Project type filter
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Client filter (filters by client name, deduped across multiple records)
  const [clientFilter, setClientFilter] = useState('all');
  const [clients, setClients] = useState([]);

  // Project status filter
  const [statusFilter, setStatusFilter] = useState('all');

  // My Tasks: filter buttons (overdue / in_progress / not_started / completed / all)
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');

  const isConsultantOrAdmin = ['ADMIN', 'CONSULTANT'].includes(user?.role?.toUpperCase());
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  // Create a color map from project types
  const projectTypeColorMap = useMemo(() => {
    const map = {};
    projectTypes.forEach(type => {
      map[type.name] = type.color || '#6B7280'; // Default gray if no color
    });
    return map;
  }, [projectTypes]);

  // Normalize a name to a stable comparison key: strip diacritics,
  // remove the unicode replacement char (â) from corrupted DB entries,
  // upper-case and trim. Used to handle "ANDRÉ" vs "ANDR�" and similar.
  const normalizeName = (raw) => {
    if (!raw) return '';
    return String(raw)
      .replace(/\uFFFD+/g, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  };

  // The "root" client key is the first word of the name. So entries like
  // "T-MINAS", "T-MINAS BERNARDONI", "T-MINAS AMPLIAÇÃO" all collapse into
  // a single dropdown entry "T-MINAS", and selecting it filters every
  // project whose client_name starts with that token.
  const rootKey = (raw) => normalizeName(raw).split(' ')[0] || '';

  // Deduplicate clients by root key. Display the shortest variant as the
  // canonical label (so "T-MINAS" shows up rather than "T-MINAS BERNARDONI").
  const uniqueClientNames = useMemo(() => {
    const byKey = new Map();
    for (const c of clients) {
      const display = (c.name || '').replace(/\uFFFD+/g, '').replace(/\s+/g, ' ').trim();
      if (!display) continue;
      const key = rootKey(display);
      if (!key) continue;
      const existing = byKey.get(key);
      if (!existing || display.length < existing.length) {
        byKey.set(key, display);
      }
    }
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [clients]);

  // Status options — canonical PT-BR only. Legacy English variants (On Hold,
  // On Hold - Agency, In Progress, Completed, Not Started) are still recognized
  // by normalizeProjectStatus() and will be shown correctly in the filtered list.
  const STATUS_OPTIONS = [
    { value: 'Não Iniciado', label: 'Não Iniciado' },
    { value: 'Em Andamento', label: 'Em Andamento' },
    { value: 'Em Espera', label: 'Em Espera' },
    { value: 'Concluído', label: 'Concluído' },
    { value: 'Cancelado', label: 'Cancelado' },
  ];

  // Filter projects by type, client (root match) and status
  const filteredProjects = useMemo(() => {
    let filtered = projects;
    if (typeFilter !== 'all') {
      filtered = filtered.filter(p => p.project_type === typeFilter);
    }
    if (clientFilter !== 'all') {
      const targetRoot = rootKey(clientFilter);
      filtered = filtered.filter(p => rootKey(p.client_name) === targetRoot);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => normalizeProjectStatus(p.status) === statusFilter);
    }
    return filtered;
  }, [projects, typeFilter, clientFilter, statusFilter]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      logger.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjectTypes = useCallback(async () => {
    try {
      const response = await api.get('/project-types');
      // Sort project types alphabetically by name
      const sortedTypes = response.data.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );
      setProjectTypes(sortedTypes);
    } catch (error) {
      logger.error('Error fetching project types:', error);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await api.get('/clients');
      // Sort clients alphabetically by name
      const sortedClients = response.data.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );
      setClients(sortedClients);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  }, []);

  const fetchMyTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const response = await api.get('/tasks/my-tasks');
      setMyTasks(response.data);
    } catch (error) {
      logger.error('Error fetching my tasks:', error);
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchProjectTypes();
    fetchClients();
  }, [fetchProjects, fetchProjectTypes, fetchClients]);

  useEffect(() => {
    if (activeTab === 'my-tasks' && myTasks.length === 0) {
      fetchMyTasks();
    }
  }, [activeTab, myTasks.length, fetchMyTasks]);

  const handleAddProjectType = async () => {
    if (!newTypeName.trim()) return;
    
    setAddingType(true);
    try {
      await api.post('/project-types', { name: newTypeName.trim() });
      toast.success('Tipo de projeto adicionado!');
      setNewTypeName('');
      setShowNewTypeInput(false);
      fetchProjectTypes();
    } catch (error) {
      logger.error('Error adding project type:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar tipo de projeto');
    } finally {
      setAddingType(false);
    }
  };

  const handleDeleteProjectType = async (typeId) => {
    if (!window.confirm('Tem certeza que deseja excluir este tipo de projeto?')) return;
    
    try {
      await api.delete(`/project-types/${typeId}`);
      toast.success('Tipo de projeto excluído!');
      fetchProjectTypes();
    } catch (error) {
      logger.error('Error deleting project type:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir tipo de projeto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/projects', formData);
      setOpen(false);
      setFormData({
        name: '',
        client_email: '',
        project_type: '',
        location: '',
        start_date: '',
        estimated_end_date: '',
      });
      fetchProjects();
    } catch (error) {
      logger.error('Error creating project:', error);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Status atualizado!');
      fetchMyTasks();
    } catch (error) {
      logger.error('Error updating task:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'bg-gray-200 text-gray-700',
      'Não Iniciado': 'bg-gray-200 text-gray-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'Em Andamento': 'bg-blue-100 text-blue-700',
      'Iniciado': 'bg-blue-100 text-blue-700',
      'On Hold': 'bg-yellow-100 text-yellow-700',
      'Em Espera': 'bg-yellow-100 text-yellow-700',
      'On Hold - Agency': 'bg-orange-100 text-orange-700',
      'Completed': 'bg-green-100 text-green-700',
      'Concluído': 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-gray-200 text-gray-700';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'not_started': 'bg-gray-200 text-gray-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700',
      'cancelled': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-200 text-gray-700';
  };

  const getTaskStatusLabel = (status) => {
    const labels = {
      'not_started': 'Não Iniciada',
      'in_progress': 'Em Andamento',
      'completed': 'Concluída',
      'cancelled': 'Cancelada',
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'text-red-600',
      'medium': 'text-yellow-600',
      'low': 'text-green-600',
    };
    return colors[priority] || 'text-gray-600';
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const isDueSoon = (deadline) => {
    if (!deadline) return false;
    const dueDate = new Date(deadline);
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Group tasks by status
  const groupedTasks = {
    overdue: myTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled' && isOverdue(t.deadline)),
    in_progress: myTasks.filter(t => t.status === 'in_progress' && !isOverdue(t.deadline)),
    not_started: myTasks.filter(t => t.status === 'not_started' && !isOverdue(t.deadline)),
    completed: myTasks.filter(t => t.status === 'completed'),
  };

  // Filtered list driven by the status-filter buttons in My Tasks
  const filteredMyTasks = taskStatusFilter === 'all'
    ? myTasks
    : (groupedTasks[taskStatusFilter] || []);

  // Group tasks by project (using the filtered list so tables react to filter buttons)
  const tasksByProject = filteredMyTasks.reduce((acc, task) => {
    const projectName = task.project_name || 'Sem Projeto';
    if (!acc[projectName]) {
      acc[projectName] = {
        project_id: task.project_id,
        project_name: projectName,
        tasks: []
      };
    }
    acc[projectName].tasks.push(task);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Projetos</h1>
            <p className="text-gray-600 mt-1">Gerencie todos os projetos de consultoria</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Project Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-52" data-testid="project-type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                {projectTypes.map((type) => (
                  <SelectItem key={type.type_id} value={type.name}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: type.color || '#6B7280' }}
                      />
                      {type.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Client Filter (deduplicated by client name) */}
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-52" data-testid="project-client-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Clientes</SelectItem>
                {uniqueClientNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Project Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-52" data-testid="project-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isConsultantOrAdmin && (
              <>
                <Button 
                  variant={activeTab === 'my-tasks' ? 'default' : 'outline'}
                  onClick={() => setActiveTab(activeTab === 'my-tasks' ? 'projects' : 'my-tasks')}
                  className={activeTab === 'my-tasks' ? 'bg-emerald-800 hover:bg-emerald-900' : ''}
                  data-testid="my-tasks-button"
                >
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Minhas Tarefas
                  {groupedTasks.overdue.length > 0 && (
                    <Badge className="ml-2 bg-red-500 text-white">{groupedTasks.overdue.length}</Badge>
                  )}
                </Button>
                
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="create-project-button">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Novo Projeto
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Criar Novo Projeto</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name">Nome do Projeto</Label>
                        <Input
                          id="name"
                          data-testid="project-name-input"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="client_email">Email do Cliente</Label>
                        <Input
                          id="client_email"
                          type="email"
                          data-testid="client-email-input"
                          value={formData.client_email}
                          onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="project_type">Tipo de Projeto</Label>
                        <div className="space-y-2">
                          <Select
                            value={formData.project_type}
                            onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                          >
                            <SelectTrigger data-testid="project-type-select">
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                            <SelectContent>
                              {projectTypes.map((type) => (
                                <SelectItem key={type.type_id} value={type.name}>
                                  <div className="flex items-center justify-between w-full">
                                    <span>{type.name}</span>
                                    {!type.is_default && isAdmin && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteProjectType(type.type_id);
                                        }}
                                        className="ml-2 text-red-500 hover:text-red-700"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {!showNewTypeInput ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowNewTypeInput(true)}
                              className="w-full"
                              data-testid="add-project-type-button"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Adicionar novo tipo
                            </Button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="Nome do novo tipo"
                                value={newTypeName}
                                onChange={(e) => setNewTypeName(e.target.value)}
                                data-testid="new-type-input"
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleAddProjectType}
                                disabled={addingType || !newTypeName.trim()}
                                className="bg-emerald-800 hover:bg-emerald-900"
                                data-testid="confirm-add-type-button"
                              >
                                {addingType ? '...' : 'Adicionar'}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowNewTypeInput(false);
                                  setNewTypeName('');
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="location">Localização</Label>
                        <Input
                          id="location"
                          data-testid="location-input"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="start_date">Data de Início</Label>
                          <Input
                            id="start_date"
                            type="date"
                            data-testid="start-date-input"
                            value={formData.start_date}
                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="estimated_end_date">Prazo Estimado</Label>
                          <Input
                            id="estimated_end_date"
                            type="date"
                            data-testid="end-date-input"
                            value={formData.estimated_end_date}
                            onChange={(e) => setFormData({ ...formData, estimated_end_date: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-project-button">
                          Criar Projeto
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* My Tasks Section */}
        {activeTab === 'my-tasks' && isConsultantOrAdmin && (
          <div className="space-y-6" data-testid="my-tasks-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <ClipboardList className="w-6 h-6" />
                Minhas Tarefas
              </h2>
              <Button variant="outline" size="sm" onClick={fetchMyTasks}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Atualizar
              </Button>
            </div>

            {loadingTasks ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
              </div>
            ) : myTasks.length === 0 ? (
              <Card className="p-12 text-center border border-gray-200">
                <ClipboardList className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma tarefa atribuída</h3>
                <p className="text-gray-500">Você não possui tarefas atribuídas no momento.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Filter Buttons (clicáveis — clique para filtrar) */}
                <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-5 gap-4">
                  <button
                    type="button"
                    onClick={() => setTaskStatusFilter('all')}
                    data-testid="filter-all-tasks"
                    className={`text-left transition-all ${taskStatusFilter === 'all' ? 'ring-2 ring-emerald-600 ring-offset-2' : 'hover:shadow-md'}`}
                  >
                    <Card className={`p-4 border ${taskStatusFilter === 'all' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-3">
                        <Filter className="w-8 h-8 text-emerald-700" />
                        <div>
                          <p className="text-2xl font-bold text-emerald-700">{myTasks.length}</p>
                          <p className="text-sm text-emerald-800">Todas</p>
                        </div>
                      </div>
                    </Card>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTaskStatusFilter(taskStatusFilter === 'overdue' ? 'all' : 'overdue')}
                    data-testid="filter-overdue-tasks"
                    className={`text-left transition-all ${taskStatusFilter === 'overdue' ? 'ring-2 ring-red-600 ring-offset-2' : 'hover:shadow-md'}`}
                  >
                    <Card className={`p-4 border ${taskStatusFilter === 'overdue' ? 'border-red-500 bg-red-100' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                        <div>
                          <p className="text-2xl font-bold text-red-600">{groupedTasks.overdue.length}</p>
                          <p className="text-sm text-red-700">Atrasadas</p>
                        </div>
                      </div>
                    </Card>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTaskStatusFilter(taskStatusFilter === 'in_progress' ? 'all' : 'in_progress')}
                    data-testid="filter-in-progress-tasks"
                    className={`text-left transition-all ${taskStatusFilter === 'in_progress' ? 'ring-2 ring-blue-600 ring-offset-2' : 'hover:shadow-md'}`}
                  >
                    <Card className={`p-4 border ${taskStatusFilter === 'in_progress' ? 'border-blue-500 bg-blue-100' : 'border-blue-200 bg-blue-50'}`}>
                      <div className="flex items-center gap-3">
                        <Clock className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="text-2xl font-bold text-blue-600">{groupedTasks.in_progress.length}</p>
                          <p className="text-sm text-blue-700">Em Andamento</p>
                        </div>
                      </div>
                    </Card>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTaskStatusFilter(taskStatusFilter === 'not_started' ? 'all' : 'not_started')}
                    data-testid="filter-not-started-tasks"
                    className={`text-left transition-all ${taskStatusFilter === 'not_started' ? 'ring-2 ring-gray-600 ring-offset-2' : 'hover:shadow-md'}`}
                  >
                    <Card className={`p-4 border ${taskStatusFilter === 'not_started' ? 'border-gray-500 bg-gray-100' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3">
                        <FolderOpen className="w-8 h-8 text-gray-600" />
                        <div>
                          <p className="text-2xl font-bold text-gray-600">{groupedTasks.not_started.length}</p>
                          <p className="text-sm text-gray-700">Não Iniciadas</p>
                        </div>
                      </div>
                    </Card>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTaskStatusFilter(taskStatusFilter === 'completed' ? 'all' : 'completed')}
                    data-testid="filter-completed-tasks"
                    className={`text-left transition-all ${taskStatusFilter === 'completed' ? 'ring-2 ring-green-600 ring-offset-2' : 'hover:shadow-md'}`}
                  >
                    <Card className={`p-4 border ${taskStatusFilter === 'completed' ? 'border-green-500 bg-green-100' : 'border-green-200 bg-green-50'}`}>
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                        <div>
                          <p className="text-2xl font-bold text-green-600">{groupedTasks.completed.length}</p>
                          <p className="text-sm text-green-700">Concluídas</p>
                        </div>
                      </div>
                    </Card>
                  </button>
                </div>

                {/* Tasks Table by Project */}
                <div className="lg:col-span-2 space-y-6">
                  {Object.values(tasksByProject).map((projectGroup) => (
                    <Card key={projectGroup.project_id} className="border border-gray-200 overflow-hidden">
                      <div className="bg-emerald-800 text-white px-4 py-3 flex items-center justify-between">
                        <h3 className="font-semibold">{projectGroup.project_name}</h3>
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => navigate(`/projects/${projectGroup.project_id}`)}
                          className="bg-white text-emerald-800 hover:bg-gray-100"
                        >
                          Ver Projeto
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Tarefa</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Fase</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Prazo</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Status</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Ação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {projectGroup.tasks.map((task) => (
                              <tr 
                                key={task.task_id} 
                                className={`border-b border-gray-100 hover:bg-gray-50 ${isOverdue(task.deadline) && task.status !== 'completed' ? 'bg-red-50' : ''}`}
                                data-testid={`task-row-${task.task_id}`}
                              >
                                <td className="py-3 px-4">
                                  <span className="font-medium text-gray-900">{task.name}</span>
                                </td>
                                <td className="py-3 px-4 text-sm text-gray-600">{task.phase_name}</td>
                                <td className="py-3 px-4 text-center">
                                  {task.deadline ? (
                                    <span className={`text-sm ${isOverdue(task.deadline) && task.status !== 'completed' ? 'text-red-600 font-semibold' : isDueSoon(task.deadline) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                                      {new Date(task.deadline).toLocaleDateString('pt-BR')}
                                      {isOverdue(task.deadline) && task.status !== 'completed' && (
                                        <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Badge className={getTaskStatusColor(task.status)}>
                                    {getTaskStatusLabel(task.status)}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Select
                                    value={task.status}
                                    onValueChange={(value) => handleUpdateTaskStatus(task.task_id, value)}
                                  >
                                    <SelectTrigger className="w-36 h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="not_started">Não Iniciada</SelectItem>
                                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                                      <SelectItem value="completed">Concluída</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Back to Projects Button */}
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => setActiveTab('projects')}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Ver Todos os Projetos
              </Button>
            </div>
          </div>
        )}

        {/* Projects Grid */}
        {activeTab === 'projects' && (
          <>
            {/* Show active filter info */}
            {typeFilter !== 'all' && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Filtrando por:</span>
                <Badge 
                  className="flex items-center gap-1"
                  style={{ 
                    backgroundColor: `${projectTypeColorMap[typeFilter]}20`,
                    color: projectTypeColorMap[typeFilter],
                    borderColor: projectTypeColorMap[typeFilter]
                  }}
                >
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: projectTypeColorMap[typeFilter] }}
                  />
                  {typeFilter}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setTypeFilter('all')}
                  className="h-6 px-2"
                >
                  <X className="w-3 h-3" />
                </Button>
                <span className="text-gray-400">({filteredProjects.length} projeto{filteredProjects.length !== 1 ? 's' : ''})</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => {
                const typeColor = projectTypeColorMap[project.project_type] || '#6B7280';
                return (
                  <Card
                    key={project.project_id}
                    className="border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
                    onClick={() => navigate(`/projects/${project.project_id}`)}
                    data-testid={`project-card-${project.project_id}`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          {project.project_number && (
                            <span
                              className="inline-block text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mb-1.5"
                              data-testid={`project-number-${project.project_id}`}
                            >
                              {project.project_number}
                            </span>
                          )}
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">{project.name}</h3>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2.5 h-2.5 rounded-full" 
                              style={{ backgroundColor: typeColor }}
                            />
                            <p className="text-sm text-gray-600">{project.project_type}</p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(project.status)}>{normalizeProjectStatus(project.status)}</Badge>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Cliente</p>
                          <p className="text-sm font-medium text-gray-900">{project.client_name}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500 mb-1">Localização</p>
                          <p className="text-sm text-gray-900">{project.location}</p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-gray-500">Progresso</p>
                            <p className="text-xs font-medium text-gray-900">{project.overall_percentage.toFixed(1)}%</p>
                          </div>
                          <Progress value={project.overall_percentage} className="h-2" />
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500">Início</p>
                            <p className="text-sm text-gray-900">{new Date(project.start_date).toLocaleDateString('pt-BR')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Prazo</p>
                            <p className="text-sm text-gray-900">{new Date(project.estimated_end_date).toLocaleDateString('pt-BR')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Colored bottom border by project type */}
                    <div 
                      className="h-1.5 w-full"
                      style={{ backgroundColor: typeColor }}
                      data-testid={`project-type-indicator-${project.project_id}`}
                    />
                  </Card>
                );
              })}
              {filteredProjects.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500">
                    {typeFilter !== 'all' 
                      ? `Nenhum projeto encontrado do tipo "${typeFilter}"`
                      : 'Nenhum projeto encontrado'
                    }
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default Projects;